/**
 * Debug utilities for tracking application debugging and performance
 * Debug mode persists across page reloads via localStorage
 *
 * To enable:  Open browser console and run: enableDebug()
 * To disable: Open browser console and run: disableDebug()
 * To view cache metrics: Open browser console and run: showCacheMetrics()
 * To reset cache metrics: Open browser console and run: resetCacheMetrics()
 */

const DEBUG_STORAGE_KEY = 'flm_debug_rpc';

// Cache metrics tracking
interface CacheStats {
  hits: number;
  misses: number;
  evictions?: number;
}

const cacheMetrics: Record<string, CacheStats> = {};

// Check if debug mode is enabled
export function isDebugEnabled(): boolean {
  if (window.DEBUG_RPC === undefined) {
    // Load from localStorage on first check
    const stored = localStorage.getItem(DEBUG_STORAGE_KEY);
    window.DEBUG_RPC = stored === 'true';
  }
  return window.DEBUG_RPC === true;
}

// Log debug information with timing
export function logDEBUG(method: string, ...args: unknown[]) {
  if (!isDebugEnabled()) return;

  const timestamp = new Date().toISOString().split('T')[1]?.split('.')[0] ?? '';
  console.log(`[DEBUG ${timestamp}]`, method, ...args);
}

// Enable debug mode from console
window.enableDebug = () => {
  window.DEBUG_RPC = true;
  localStorage.setItem(DEBUG_STORAGE_KEY, 'true');
  console.log('✅ Debug logging enabled (persists across reloads).');
};

window.disableDebug = () => {
  window.DEBUG_RPC = false;
  localStorage.removeItem(DEBUG_STORAGE_KEY);
  console.log('❌ Debug logging disabled.');
};

// Track cache hit
export function logCacheHit(cacheName: string) {
  if (!isDebugEnabled()) return;

  if (!cacheMetrics[cacheName]) {
    cacheMetrics[cacheName] = { hits: 0, misses: 0 };
  }
  cacheMetrics[cacheName].hits++;
}

// Track cache miss
export function logCacheMiss(cacheName: string) {
  if (!isDebugEnabled()) return;

  if (!cacheMetrics[cacheName]) {
    cacheMetrics[cacheName] = { hits: 0, misses: 0 };
  }
  cacheMetrics[cacheName].misses++;
}

// Track cache eviction (for LRU caches)
export function logCacheEviction(cacheName: string) {
  if (!isDebugEnabled()) return;

  if (!cacheMetrics[cacheName]) {
    cacheMetrics[cacheName] = { hits: 0, misses: 0, evictions: 0 };
  }
  if (cacheMetrics[cacheName].evictions !== undefined) {
    cacheMetrics[cacheName].evictions!++;
  } else {
    cacheMetrics[cacheName].evictions = 1;
  }
}

// Show cache metrics dashboard
window.showCacheMetrics = () => {
  if (Object.keys(cacheMetrics).length === 0) {
    console.log('📊 No cache metrics collected yet. Make sure debug logging is enabled.');
    return;
  }

  console.log('\n📊 ═══════════════════════════════════════════════════════');
  console.log('   CACHE METRICS DASHBOARD');
  console.log('═══════════════════════════════════════════════════════\n');

  // Calculate totals
  let totalHits = 0;
  let totalMisses = 0;
  let totalEvictions = 0;

  const sortedCaches = Object.entries(cacheMetrics).sort((a, b) => {
    const totalA = a[1].hits + a[1].misses;
    const totalB = b[1].hits + b[1].misses;
    return totalB - totalA;
  });

  for (const [cacheName, stats] of sortedCaches) {
    const total = stats.hits + stats.misses;
    const hitRate = total > 0 ? ((stats.hits / total) * 100).toFixed(1) : '0.0';

    totalHits += stats.hits;
    totalMisses += stats.misses;
    if (stats.evictions) totalEvictions += stats.evictions;

    console.log(`📦 ${cacheName}`);
    console.log(`   Hits: ${stats.hits.toLocaleString()} | Misses: ${stats.misses.toLocaleString()} | Hit Rate: ${hitRate}%`);
    if (stats.evictions && stats.evictions > 0) {
      console.log(`   Evictions: ${stats.evictions.toLocaleString()} (LRU)`);
    }
    console.log('');
  }

  const totalRequests = totalHits + totalMisses;
  const overallHitRate = totalRequests > 0 ? ((totalHits / totalRequests) * 100).toFixed(1) : '0.0';

  console.log('───────────────────────────────────────────────────────');
  console.log('📈 OVERALL STATISTICS');
  console.log(`   Total Requests: ${totalRequests.toLocaleString()}`);
  console.log(`   Cache Hits: ${totalHits.toLocaleString()}`);
  console.log(`   Cache Misses: ${totalMisses.toLocaleString()}`);
  console.log(`   Hit Rate: ${overallHitRate}%`);
  if (totalEvictions > 0) {
    console.log(`   Total Evictions: ${totalEvictions.toLocaleString()}`);
  }
  console.log('═══════════════════════════════════════════════════════\n');
};

// Reset cache metrics
window.resetCacheMetrics = () => {
  Object.keys(cacheMetrics).forEach((key) => delete cacheMetrics[key]);
  console.log('🔄 Cache metrics reset.');
};
